import {
  type ReactNode,
  type ElementType,
  type MouseEvent,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  ChevronUp,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  Filter,
  Download,
  X,
  Inbox,
} from "lucide-react";
import { EmptyState } from "./EmptyState";
import { Skeleton } from "./Skeleton";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  render?: (item: T) => ReactNode;
  width?: string;
  align?: "left" | "center" | "right";
}

export interface BatchAction<T> {
  label: string;
  icon?: ElementType;
  onClick: (items: T[]) => void;
  variant?: "danger" | "default";
}

export interface ContextMenuItem<T> {
  label: string;
  icon?: ElementType;
  onClick: (item: T) => void;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  pageSize?: number;
  selectable?: boolean;
  onSelectionChange?: (selected: T[]) => void;
  batchActions?: BatchAction<T>[];
  contextMenu?: ContextMenuItem<T>[];
  onRowClick?: (item: T) => void;
  exportable?: boolean;
  exportFilename?: string;
  emptyMessage?: string;
  emptyIcon?: ElementType;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type SortDir = "asc" | "desc" | null;

const PAGE_SIZES = [10, 25, 50, 100] as const;

const LS_KEY = "qimei-datatable-pagesize";

function readStoredPageSize(fallback: number): number {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v && PAGE_SIZES.includes(Number(v) as (typeof PAGE_SIZES)[number])) return Number(v);
  } catch { /* SSR / privacy */ }
  return fallback;
}

function rawValue(item: Record<string, unknown>, key: string): unknown {
  return item[key];
}

function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  if (typeof a === "number" && typeof b === "number") return a - b;

  const sa = String(a);
  const sb = String(b);

  const da = Date.parse(sa);
  const db = Date.parse(sb);
  if (!isNaN(da) && !isNaN(db)) return da - db;

  return sa.localeCompare(sb, "zh-Hant");
}

function escapeCSV(val: unknown): string {
  if (val == null) return "";
  const s = String(val);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FilterDropdown({
  value,
  onChange,
  onClose,
}: {
  value: string;
  onChange: (v: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    function handle(e: globalThis.MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute top-full left-0 mt-1 z-40 bg-white border border-slate-200 rounded-lg shadow-lg p-2 min-w-[180px]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="text"
          className="input text-xs py-1 px-2"
          placeholder="篩選..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
        />
        {value && (
          <button
            className="p-1 text-slate-400 hover:text-slate-600"
            onClick={() => onChange("")}
          >
            <X size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

function ContextMenuPopup<T>({
  x,
  y,
  item,
  items,
  onClose,
}: {
  x: number;
  y: number;
  item: T;
  items: ContextMenuItem<T>[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handle(e: globalThis.MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [onClose]);

  useEffect(() => {
    function handle() { onClose(); }
    window.addEventListener("scroll", handle, true);
    return () => window.removeEventListener("scroll", handle, true);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="fixed z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[140px] animate-scale-in"
      style={{ left: x, top: y }}
    >
      {items.map((mi, idx) => {
        const Icon = mi.icon;
        return (
          <button
            key={idx}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
            onClick={() => { mi.onClick(item); onClose(); }}
          >
            {Icon && <Icon size={14} className="text-slate-400" />}
            {mi.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DataTable
// ---------------------------------------------------------------------------

export function DataTable<T extends { id: number | string }>({
  columns,
  data,
  loading = false,
  pageSize: pageSizeProp = 10,
  selectable = false,
  onSelectionChange,
  batchActions,
  contextMenu,
  onRowClick,
  exportable = false,
  exportFilename,
  emptyMessage = "沒有資料",
  emptyIcon,
}: DataTableProps<T>) {
  // -- Sort --
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  // -- Filter --
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [openFilter, setOpenFilter] = useState<string | null>(null);

  // -- Pagination --
  const [perPage, setPerPage] = useState(() => readStoredPageSize(pageSizeProp));
  const [page, setPage] = useState(0);

  // -- Selection --
  const [selectedIds, setSelectedIds] = useState<Set<number | string>>(new Set());

  // -- Context menu --
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; item: T } | null>(null);

  // Reset page when data, filters, or sort changes
  useEffect(() => { setPage(0); }, [data, filters, sortKey, sortDir]);

  // Reset selection when data changes
  useEffect(() => { setSelectedIds(new Set()); }, [data]);

  // Notify parent of selection changes
  useEffect(() => {
    if (!onSelectionChange) return;
    const selected = data.filter((d) => selectedIds.has(d.id));
    onSelectionChange(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]);

  // -- Filtered data --
  const filteredData = useMemo(() => {
    const activeFilters = Object.entries(filters).filter(([, v]) => v.trim() !== "");
    if (!activeFilters.length) return data;

    return data.filter((item) =>
      activeFilters.every(([key, term]) => {
        const val = rawValue(item as unknown as Record<string, unknown>, key);
        if (val == null) return false;
        return String(val).toLowerCase().includes(term.toLowerCase());
      })
    );
  }, [data, filters]);

  // -- Sorted data --
  const sortedData = useMemo(() => {
    if (!sortKey || !sortDir) return filteredData;
    const arr = [...filteredData];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const va = rawValue(a as unknown as Record<string, unknown>, sortKey);
      const vb = rawValue(b as unknown as Record<string, unknown>, sortKey);
      return dir * compareValues(va, vb);
    });
    return arr;
  }, [filteredData, sortKey, sortDir]);

  // -- Pagination derived --
  const totalItems = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
  const safePage = Math.min(page, totalPages - 1);
  const pagedData = sortedData.slice(safePage * perPage, (safePage + 1) * perPage);
  const rangeStart = totalItems === 0 ? 0 : safePage * perPage + 1;
  const rangeEnd = Math.min((safePage + 1) * perPage, totalItems);

  // -- Page IDs for select-all --
  const pageIds = useMemo(() => new Set(pagedData.map((d) => d.id)), [pagedData]);
  const allPageSelected = pagedData.length > 0 && pagedData.every((d) => selectedIds.has(d.id));
  const somePageSelected = pagedData.some((d) => selectedIds.has(d.id));

  // -- Handlers --
  const handleSort = useCallback((key: string) => {
    if (sortKey === key) {
      if (sortDir === "asc") setSortDir("desc");
      else if (sortDir === "desc") { setSortKey(null); setSortDir(null); }
      else setSortDir("asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }, [sortKey, sortDir]);

  const handleFilter = useCallback((key: string, val: string) => {
    setFilters((prev) => ({ ...prev, [key]: val }));
  }, []);

  const handlePageSize = useCallback((size: number) => {
    setPerPage(size);
    setPage(0);
    try { localStorage.setItem(LS_KEY, String(size)); } catch { /* noop */ }
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allPageSelected, pageIds]);

  const toggleSelect = useCallback((id: number | string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleContextMenu = useCallback((e: MouseEvent, item: T) => {
    if (!contextMenu?.length) return;
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, item });
  }, [contextMenu]);

  const handleExport = useCallback(() => {
    const header = columns.map((c) => c.label).join(",");
    const rows = sortedData.map((item) =>
      columns.map((c) => escapeCSV(rawValue(item as unknown as Record<string, unknown>, c.key))).join(",")
    );
    const csv = "\uFEFF" + [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const date = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `${exportFilename ?? "export"}_${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [columns, sortedData, exportFilename]);

  const selectedItems = useMemo(
    () => data.filter((d) => selectedIds.has(d.id)),
    [data, selectedIds]
  );

  // -- Active filter count --
  const activeFilterCount = Object.values(filters).filter((v) => v.trim() !== "").length;

  // -- Pagination page numbers --
  const pageNumbers = useMemo(() => {
    const pages: (number | "...")[] = [];
    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      pages.push(0);
      if (safePage > 2) pages.push("...");
      const start = Math.max(1, safePage - 1);
      const end = Math.min(totalPages - 2, safePage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (safePage < totalPages - 3) pages.push("...");
      pages.push(totalPages - 1);
    }
    return pages;
  }, [totalPages, safePage]);

  // -----------------------------------------------------------------------
  // Loading
  // -----------------------------------------------------------------------
  if (loading) {
    return <Skeleton variant="table" rows={perPage > 25 ? 10 : 5} />;
  }

  // -----------------------------------------------------------------------
  // Empty
  // -----------------------------------------------------------------------
  if (!data.length) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white">
        <EmptyState
          icon={emptyIcon ?? Inbox}
          title={emptyMessage}
        />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  const alignClass = (a?: "left" | "center" | "right") =>
    a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

  return (
    <div className="space-y-0">
      {/* Toolbar */}
      {(exportable || activeFilterCount > 0) && (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <span className="text-xs text-slate-500">
                {activeFilterCount} 個篩選條件生效
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {exportable && (
              <button
                className="btn text-xs py-1.5 px-3"
                onClick={handleExport}
              >
                <Download size={14} />
                匯出 CSV
              </button>
            )}
          </div>
        </div>
      )}

      {/* Batch action bar */}
      {selectable && selectedIds.size > 0 && batchActions?.length ? (
        <div className="sticky top-0 z-30 flex items-center gap-3 px-4 py-2.5 mb-0 rounded-t-xl border border-qimei-200 bg-qimei-50">
          <span className="text-sm font-medium text-qimei-700">
            已選 {selectedIds.size} 筆
          </span>
          <div className="flex items-center gap-2 ml-auto">
            {batchActions.map((action, idx) => {
              const Icon = action.icon;
              return (
                <button
                  key={idx}
                  className={`btn text-xs py-1 px-2.5 ${action.variant === "danger" ? "btn-danger" : ""}`}
                  onClick={() => action.onClick(selectedItems)}
                >
                  {Icon && <Icon size={14} />}
                  {action.label}
                </button>
              );
            })}
            <button
              className="btn text-xs py-1 px-2.5"
              onClick={() => setSelectedIds(new Set())}
            >
              取消選取
            </button>
          </div>
        </div>
      ) : null}

      {/* Table */}
      <div
        className={`overflow-x-auto bg-white border border-slate-200 ${
          selectable && selectedIds.size > 0 && batchActions?.length
            ? "rounded-b-xl border-t-0"
            : "rounded-xl"
        }`}
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              {selectable && (
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 accent-qimei-500"
                    checked={allPageSelected}
                    ref={(el) => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                    onChange={toggleSelectAll}
                  />
                </th>
              )}
              {columns.map((col) => {
                const isSorted = sortKey === col.key;
                const canSort = col.sortable !== false;
                const canFilter = col.filterable === true;
                const hasFilter = (filters[col.key] ?? "").trim() !== "";

                return (
                  <th
                    key={col.key}
                    className={`px-4 py-3 text-[11px] font-medium text-slate-500 uppercase tracking-wider ${alignClass(col.align)} ${canSort ? "cursor-pointer select-none" : ""} relative`}
                    style={col.width ? { width: col.width } : undefined}
                    onClick={() => canSort && handleSort(col.key)}
                  >
                    <div className={`flex items-center gap-1 ${col.align === "right" ? "justify-end" : col.align === "center" ? "justify-center" : ""}`}>
                      <span>{col.label}</span>

                      {/* Sort indicator */}
                      {canSort && (
                        <span className={`inline-flex flex-col -space-y-1 ${isSorted ? "text-qimei-500" : "text-slate-300"}`}>
                          <ChevronUp size={12} className={isSorted && sortDir === "asc" ? "text-qimei-500" : ""} />
                          <ChevronDown size={12} className={isSorted && sortDir === "desc" ? "text-qimei-500" : ""} />
                        </span>
                      )}

                      {/* Filter button */}
                      {canFilter && (
                        <button
                          className={`ml-0.5 p-0.5 rounded hover:bg-slate-200 transition-colors ${hasFilter ? "text-qimei-500" : "text-slate-400"}`}
                          onClick={(e) => { e.stopPropagation(); setOpenFilter(openFilter === col.key ? null : col.key); }}
                        >
                          <Filter size={11} />
                        </button>
                      )}
                    </div>

                    {/* Filter dropdown */}
                    {canFilter && openFilter === col.key && (
                      <FilterDropdown
                        value={filters[col.key] ?? ""}
                        onChange={(v) => handleFilter(col.key, v)}
                        onClose={() => setOpenFilter(null)}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pagedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="text-center py-12 text-slate-400 text-sm"
                >
                  篩選條件下沒有符合的資料
                </td>
              </tr>
            ) : (
              pagedData.map((item) => {
                const isSelected = selectedIds.has(item.id);
                return (
                  <tr
                    key={item.id as string | number}
                    onClick={() => onRowClick?.(item)}
                    onContextMenu={(e) => handleContextMenu(e, item)}
                    className={`border-b border-slate-50 last:border-0 transition-colors ${
                      isSelected ? "bg-qimei-50" : ""
                    } ${onRowClick ? "cursor-pointer" : ""} hover:bg-slate-50`}
                  >
                    {selectable && (
                      <td className="w-10 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 accent-qimei-500"
                          checked={isSelected}
                          onChange={() => toggleSelect(item.id)}
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-4 py-3 ${alignClass(col.align)}`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.render
                          ? col.render(item)
                          : String((item as unknown as Record<string, unknown>)[col.key] ?? "-")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="flex items-center justify-between pt-3 text-sm">
          <div className="flex items-center gap-3 text-slate-500">
            <span>
              顯示 {rangeStart}-{rangeEnd} 共 {totalItems} 筆
            </span>
            <select
              className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white"
              value={perPage}
              onChange={(e) => handlePageSize(Number(e.target.value))}
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>{s} 筆/頁</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1">
            <button
              className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              disabled={safePage === 0}
              onClick={() => setPage(0)}
            >
              <ChevronsLeft size={16} className="text-slate-600" />
            </button>
            <button
              className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              disabled={safePage === 0}
              onClick={() => setPage(safePage - 1)}
            >
              <ChevronLeft size={16} className="text-slate-600" />
            </button>

            {pageNumbers.map((p, idx) =>
              p === "..." ? (
                <span key={`dot-${idx}`} className="px-1 text-slate-400">...</span>
              ) : (
                <button
                  key={p}
                  className={`min-w-[28px] h-7 rounded-md text-xs font-medium transition-colors ${
                    p === safePage
                      ? "bg-qimei-500 text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                  onClick={() => setPage(p as number)}
                >
                  {(p as number) + 1}
                </button>
              )
            )}

            <button
              className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              disabled={safePage >= totalPages - 1}
              onClick={() => setPage(safePage + 1)}
            >
              <ChevronRight size={16} className="text-slate-600" />
            </button>
            <button
              className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              disabled={safePage >= totalPages - 1}
              onClick={() => setPage(totalPages - 1)}
            >
              <ChevronsRight size={16} className="text-slate-600" />
            </button>
          </div>
        </div>
      )}

      {/* Context menu */}
      {ctxMenu && contextMenu?.length ? (
        <ContextMenuPopup
          x={ctxMenu.x}
          y={ctxMenu.y}
          item={ctxMenu.item}
          items={contextMenu}
          onClose={() => setCtxMenu(null)}
        />
      ) : null}
    </div>
  );
}
